import { idForSlug } from './article-slugs.js';
import type { Article } from './article-types.js';
import { DEFAULT_LOCALE, type Locale } from './locales.js';

/**
 * The article registry.
 *
 * Same shape as the page-content registry and for the same reason: the prerenderer loads
 * every language because it renders every language, and the browser loads only the one on
 * screen. The difference is that a language is allowed to have no articles, or only some
 * of them, which is what makes publishing before translating possible.
 */

const BY_LOCALE = new Map<Locale, readonly Article[]>();

export function registerArticles(locale: Locale, articles: readonly Article[]): void {
  BY_LOCALE.set(locale, articles);
}

/** Newest first, which is the order the index shows them in. */
export function articlesFor(locale: Locale = DEFAULT_LOCALE): readonly Article[] {
  const articles = BY_LOCALE.get(locale) ?? [];
  return [...articles].sort((a, b) => b.published.localeCompare(a.published));
}

export function articleById(locale: Locale, id: string): Article | undefined {
  return BY_LOCALE.get(locale)?.find((article) => article.id === id);
}

export function articleBySlug(locale: Locale, slug: string): Article | undefined {
  const id = idForSlug(slug, locale);
  return id === undefined ? undefined : articleById(locale, id);
}
